import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Phone,
  Download,
  ArrowLeft,
  BarChart3,
  CheckCircle,
  RotateCcw,
  AlertTriangle,
  Package,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  CalendarCheck,
  Info,
  Mail,
  Loader2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import { COMPANY_INFO } from '@/lib/constants';
import { generateEstimationPdf } from '@/utils/generateEstimationPdf';
import { uploadEstimatePdf } from '@/service/estimationService';
import type {
  EstimateResponse,
  AnalyzeResponse,
  ProjectInputs,
  RevenueModel,
  Platform,
  ItemPricing,
} from '@/types/estimation';

// ─── Labels ────────────────────────────────────────────────────────

const REVENUE_LABELS: Record<RevenueModel, string> = {
  subscription: 'Abonnement',
  ads: 'Publicité',
  freemium: 'Freemium',
  commission: 'Commission',
  one_time: 'Achat unique',
  usage_based: "Facturation à l'usage",
};

const PLATFORM_LABELS: Record<Platform, string> = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
  desktop: 'Bureau',
};

// ─── Helpers ────────────────────────────────────────────────────────

const formatPrice = (n: number) => Math.round(n).toLocaleString('fr-FR');

const PriceRange: React.FC<{ pricing?: ItemPricing }> = ({ pricing }) => {
  if (!pricing) return null;
  return (
    <span className="text-xs font-medium text-orcsys-600 whitespace-nowrap">
      {formatPrice(pricing.price_low)} € – {formatPrice(pricing.price_high)} €
    </span>
  );
};

const ExplanationText: React.FC<{ text: string | null | undefined }> = ({ text }) => {
  if (!text) return null;
  return (
    <p className="text-xs text-slate-500 italic mt-1 leading-relaxed">
      <Info className="w-3 h-3 inline mr-1 -mt-0.5 text-slate-400" />
      {text}
    </p>
  );
};

// ─── Component ─────────────────────────────────────────────────────

interface EstimationResultProps {
  estimate: EstimateResponse;
  analysis: AnalyzeResponse | null;
  inputs: ProjectInputs;
  onReset: () => void;
  onModify: () => void;
}

const EstimationResult: React.FC<EstimationResultProps> = ({
  estimate,
  analysis,
  inputs,
  onReset,
  onModify,
}) => {
  const maxPhaseWeeks = Math.max(
    ...estimate.timeline_phases.map((p) => p.duration_weeks),
    0.5
  );

  const pricing = estimate.pricing;

  // All features expanded by default
  const sf = estimate.selected_features;
  const allFeatureIds = React.useMemo(() => {
    const ids = new Set<string>();
    sf?.matched_features?.forEach((f) => {
      if (f.sub_features && f.sub_features.length > 0) ids.add(f.id);
    });
    sf?.generated_features?.forEach((f) => {
      if (f.sub_features && f.sub_features.length > 0) ids.add(f.temp_id);
    });
    // Also expand generated addons with sub_items
    sf?.generated_addons?.forEach((ga) => {
      if (ga.sub_items && ga.sub_items.length > 0) ids.add(ga.temp_id);
    });
    return ids;
  }, [sf]);

  const [expandedFeatures, setExpandedFeatures] = React.useState<Set<string>>(allFeatureIds);
  const [emailModalOpen, setEmailModalOpen] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [downloading, setDownloading] = React.useState(false);

  const toggleFeatureExpand = (id: string) => {
    setExpandedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openDownloadModal = () => {
    setEmailModalOpen(true);
  };

  const handleDownloadWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setDownloading(true);
    try {
      toast.loading('Génération du PDF en cours...', { id: 'download' });
      const pdfBlob = await generateEstimationPdf(estimate, analysis, inputs);

      // Upload to backend (fire & forget — don't block the download)
      uploadEstimatePdf(estimate.id, email.trim(), pdfBlob).catch(() => {
        // Silently ignore upload errors — the user already got their PDF
      });

      toast.success('Estimation téléchargée !', { id: 'download' });
      setEmailModalOpen(false);
    } catch {
      toast.error('Erreur lors du téléchargement', { id: 'download' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center pt-12 md:pt-16"
      >
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Votre estimation personnalisée
        </h2>
        <p className="text-base sm:text-lg text-slate-600">
          Voici le résultat de l'analyse de votre projet
        </p>
      </motion.div>

      {/* ═══ Disclaimer Banner ═══ */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:p-6 flex items-start gap-3 sm:gap-4"
      >
        <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-900">
            Ceci est une estimation indicative, pas un devis
          </p>
          <p className="text-sm text-amber-800 mt-1">
            Les montants et délais présentés sont des estimations basées sur les informations
            fournies. Ils ne constituent pas un devis contractuel. Pour obtenir un devis
            personnalisé et détaillé, contactez-nous.
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* ═══ Main Content (left 2/3) ═══ */}
        <div className="lg:col-span-2 space-y-6">
          {/* ── Price Hero ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl overflow-hidden"
          >
            {pricing ? (
              <>
                {/* Market rate — prominent on top */}
                <div className="bg-slate-800 px-4 sm:px-8 py-4 sm:py-6 text-center">
                  <p className="text-slate-400 text-xs sm:text-sm mb-1">
                    Tarif moyen constaté en agence
                  </p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-white line-through decoration-red-400 decoration-2">
                    {pricing.market_rate.toLocaleString('fr-FR')} €
                  </p>
                </div>

                {/* Our range — below */}
                <div className="bg-gradient-to-br from-orcsys-500 to-orcsys-600 px-4 sm:px-8 py-6 sm:py-8 text-white text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <TrendingDown className="w-5 h-5 text-green-300" />
                    <p className="text-orcsys-100 font-medium">
                      Si réalisé par notre équipe
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 md:gap-6">
                    <div>
                      <p className="text-3xl sm:text-4xl md:text-5xl font-bold">
                        {pricing.price_low.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <span className="text-xl sm:text-2xl text-orcsys-200 font-light">—</span>
                    <div>
                      <p className="text-3xl sm:text-4xl md:text-5xl font-bold">
                        {pricing.price_high.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                  </div>
                  <p className="text-orcsys-200 text-xs sm:text-sm mt-3">
                    Fourchette estimée selon la complexité du projet
                  </p>

                  <div className="flex flex-wrap items-center justify-center mt-6 pt-5 border-t border-white/20 gap-6">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-orcsys-200" />
                      <span className="font-semibold">
                        ~{estimate.total_duration_weeks} semaines
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                    <button
                      onClick={openDownloadModal}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-orcsys-600 font-semibold text-sm hover:bg-white/90 transition-all shadow-sm"
                    >
                      <Download className="w-4 h-4" />
                      <span>Télécharger</span>
                    </button>
                    <a
                      href={`tel:${COMPANY_INFO.phone}`}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-orcsys-600 font-semibold text-sm hover:bg-white/90 transition-all shadow-sm"
                    >
                      <Phone className="w-4 h-4" />
                      <span>Nous appeler</span>
                    </a>
                    <a
                      href="/booking"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-orcsys-600 font-semibold text-sm hover:bg-white/90 transition-all shadow-sm"
                    >
                      <CalendarCheck className="w-4 h-4" />
                      <span>Prendre RDV</span>
                    </a>
                  </div>
                </div>
              </>
            ) : (
              /* Fallback to single price */
              <div className="bg-gradient-to-br from-orcsys-500 to-orcsys-600 px-4 sm:px-8 py-6 sm:py-8 text-white text-center">
                <p className="text-orcsys-100 mb-2">Estimation</p>
                <div className="flex items-baseline justify-center">
                  <span className="text-3xl sm:text-5xl font-bold">
                    {estimate.price.toLocaleString('fr-FR')}
                  </span>
                  <span className="text-xl sm:text-2xl ml-2 text-orcsys-100">€</span>
                </div>
                <div className="flex flex-wrap items-center justify-center mt-6 pt-5 border-t border-white/20 gap-6">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-orcsys-200" />
                    <span className="font-semibold">
                      ~{estimate.total_duration_weeks} semaines
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                  <button
                    onClick={openDownloadModal}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-orcsys-600 font-semibold text-sm hover:bg-white/90 transition-all shadow-sm"
                  >
                    <Download className="w-4 h-4" />
                    <span>Télécharger</span>
                  </button>
                  <a
                    href={`tel:${COMPANY_INFO.phone}`}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-orcsys-600 font-semibold text-sm hover:bg-white/90 transition-all shadow-sm"
                  >
                    <Phone className="w-4 h-4" />
                    <span>Nous appeler</span>
                  </a>
                  <a
                    href="/booking"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-orcsys-600 font-semibold text-sm hover:bg-white/90 transition-all shadow-sm"
                  >
                    <CalendarCheck className="w-4 h-4" />
                    <span>Prendre RDV</span>
                  </a>
                </div>
              </div>
            )}
          </motion.div>

          {/* ── Project Info ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-xl p-4 sm:p-8"
          >
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">Détails du projet</h3>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {inputs.platforms.map((p) => (
                  <span
                    key={p}
                    className="px-3 py-1 bg-orcsys-100 text-orcsys-700 rounded-full text-sm font-medium"
                  >
                    {PLATFORM_LABELS[p] || p}
                  </span>
                ))}
                {inputs.revenueModels.map((rm) => (
                  <span
                    key={rm}
                    className="px-3 py-1 bg-orcsys-100 text-orcsys-700 rounded-full text-sm font-medium"
                  >
                    {REVENUE_LABELS[rm] || rm}
                  </span>
                ))}
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                {inputs.projectDescription}
              </p>
              {analysis && (
                <p className="text-sm text-slate-500">
                  Catégorie :{' '}
                  <span className="font-medium">{analysis.project_category}</span>
                </p>
              )}
            </div>
          </motion.div>

          {/* ── Included Features ── */}
          {sf && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-2xl shadow-xl p-4 sm:p-8"
            >
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 sm:mb-6 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-orcsys-500" />
                Fonctionnalités incluses
              </h3>

              <div className="space-y-3">
                {/* Matched features */}
                {sf.matched_features?.map((feature) => {
                  const expanded = expandedFeatures.has(feature.id);
                  const hasSubs = feature.sub_features && feature.sub_features.length > 0;
                  return (
                    <div
                      key={feature.id}
                      className="rounded-xl border border-slate-200 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => hasSubs && toggleFeatureExpand(feature.id)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <CheckCircle className="w-5 h-5 text-orcsys-500 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-slate-900 text-sm">
                                {feature.name}
                              </h4>
                              <PriceRange pricing={feature.pricing} />
                            </div>
                            {feature.description && (
                              <p className="text-xs text-slate-500 truncate">
                                {feature.description}
                              </p>
                            )}
                            <ExplanationText text={feature.explanation} />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          {hasSubs &&
                            (expanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ))}
                        </div>
                      </button>
                      {expanded && hasSubs && (
                        <div className="px-3 sm:px-4 pb-4 pt-1 border-t border-slate-100 ml-4 sm:ml-8 space-y-1">
                          {feature.sub_features.map((subf, si) => (
                            <div
                              key={si}
                              className="flex items-center py-1.5 text-sm"
                            >
                              <span className="text-slate-600">• {subf.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Generated features */}
                {sf.generated_features?.map((feature) => {
                  const expanded = expandedFeatures.has(feature.temp_id);
                  const hasSubs = feature.sub_features && feature.sub_features.length > 0;
                  return (
                    <div
                      key={feature.temp_id}
                      className="rounded-xl border border-slate-200 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => hasSubs && toggleFeatureExpand(feature.temp_id)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <CheckCircle className="w-5 h-5 text-orcsys-500 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-slate-900 text-sm">{feature.name}</h4>
                              <PriceRange pricing={feature.pricing} />
                            </div>
                            {feature.description && (
                              <p className="text-xs text-slate-500 truncate">
                                {feature.description}
                              </p>
                            )}
                            <ExplanationText text={feature.explanation} />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          {hasSubs &&
                            (expanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ))}
                        </div>
                      </button>
                      {expanded && hasSubs && (
                        <div className="px-3 sm:px-4 pb-4 pt-1 border-t border-slate-100 ml-4 sm:ml-8 space-y-1">
                          {feature.sub_features.map((subf, si) => (
                            <div key={si} className="py-1.5">
                              <div className="flex items-center text-sm">
                                <span className="text-slate-600">• {subf.name}</span>
                              </div>
                              {subf.explanation && (
                                <p className="text-xs text-slate-400 italic ml-3 mt-0.5">
                                  {subf.explanation}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add-ons section */}
              {((sf.addons && sf.addons.length > 0) ||
                (sf.generated_addons && sf.generated_addons.length > 0)) && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                    <Package className="w-5 h-5 mr-2 text-orcsys-500" />
                    Extensions
                  </h4>
                  <div className="space-y-2">
                    {/* Catalog addons */}
                    {sf.addons?.map((addon) => (
                      <div
                        key={addon.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Package className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900">{addon.name}</p>
                            {addon.description && (
                              <p className="text-xs text-slate-500 truncate">
                                {addon.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <PriceRange pricing={addon.pricing} />
                      </div>
                    ))}

                    {/* Generated addons — with explanation, sub_items, pricing */}
                    {sf.generated_addons?.map((addon) => {
                      const expanded = expandedFeatures.has(addon.temp_id);
                      const hasSubItems = addon.sub_items && addon.sub_items.length > 0;

                      return (
                        <div
                          key={addon.temp_id}
                          className="rounded-lg bg-slate-50 overflow-hidden"
                        >
                          <div className="flex items-start justify-between p-3 gap-3">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <Package className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-medium text-slate-900">{addon.name}</p>
                                </div>
                                {addon.description && (
                                  <p className="text-xs text-slate-500">{addon.description}</p>
                                )}
                                <ExplanationText text={addon.explanation} />

                                {/* Toggle sub-items */}
                                {hasSubItems && (
                                  <button
                                    type="button"
                                    onClick={() => toggleFeatureExpand(addon.temp_id)}
                                    className="mt-1.5 text-xs text-orcsys-600 hover:text-orcsys-700 flex items-center gap-1"
                                  >
                                    {expanded ? (
                                      <ChevronUp className="w-3 h-3" />
                                    ) : (
                                      <ChevronDown className="w-3 h-3" />
                                    )}
                                    {expanded ? 'Masquer le détail' : 'Voir le détail'}
                                  </button>
                                )}
                              </div>
                            </div>
                            <PriceRange pricing={addon.pricing} />
                          </div>

                          {/* Expanded sub-items */}
                          {expanded && hasSubItems && (
                            <div className="px-3 sm:px-4 pb-3 pt-1 border-t border-slate-200 ml-3 sm:ml-7 space-y-1.5">
                              {addon.sub_items!.map((si, idx) => (
                                <div key={idx} className="py-1">
                                  <div className="flex items-center text-sm">
                                    <span className="text-slate-600">• {si.name}</span>
                                  </div>
                                  {si.explanation && (
                                    <p className="text-xs text-slate-400 italic ml-3 mt-0.5">
                                      {si.explanation}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Additional sub-features */}
              {sf.additional_sub_features && sf.additional_sub_features.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="text-lg font-semibold text-slate-900 mb-4">
                    Options avancées incluses
                  </h4>
                  <div className="space-y-2">
                    {sf.additional_sub_features.map((group, gi) => (
                      <div key={gi} className="space-y-1">
                        {group.pricing && (
                          <div className="flex items-center justify-end">
                            <PriceRange pricing={group.pricing} />
                          </div>
                        )}
                        {group.additional_sub_features.map((asf, i) => (
                          <div key={i} className="py-1">
                            <div className="flex items-center text-sm">
                              <span className="text-slate-600">• {asf.name}</span>
                            </div>
                            {asf.explanation && (
                              <p className="text-xs text-slate-400 italic ml-3 mt-0.5">
                                {asf.explanation}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

        </div>

        {/* ═══ Sidebar (right 1/3) ═══ */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-orcsys-500" />
              Résumé
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Durée estimée</span>
                <span className="font-medium text-slate-900">
                  ~{estimate.total_duration_weeks} sem
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Phases</span>
                <span className="font-medium text-slate-900">
                  {estimate.timeline_phases.length}
                </span>
              </div>
              {sf && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Fonctionnalités</span>
                    <span className="font-medium text-slate-900">
                      {(sf.matched_features?.length || 0) +
                        (sf.generated_features?.length || 0)}
                    </span>
                  </div>
                  {((sf.addons?.length || 0) + (sf.generated_addons?.length || 0)) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Extensions</span>
                      <span className="font-medium text-slate-900">
                        {(sf.addons?.length || 0) + (sf.generated_addons?.length || 0)}
                      </span>
                    </div>
                  )}
                </>
              )}
              <div className="h-px bg-slate-200 my-2" />
              {pricing ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Tarif agence</span>
                    <span className="font-medium text-red-400 line-through">
                      {pricing.market_rate.toLocaleString('fr-FR')} €
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                    <span className="font-semibold text-slate-900 text-sm">Notre fourchette</span>
                    <span className="font-bold text-orcsys-600 text-sm sm:text-base text-right">
                      {pricing.price_low.toLocaleString('fr-FR')} – {pricing.price_high.toLocaleString('fr-FR')} €
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-900">Estimation</span>
                  <span className="font-bold text-orcsys-600 text-lg">
                    {estimate.price.toLocaleString('fr-FR')} €
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Timeline */}
          {estimate.timeline_phases.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-xl p-6"
            >
              <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center">
                <Calendar className="w-4 h-4 mr-1.5 text-orcsys-500" />
                Calendrier
              </h3>
              <div className="space-y-2.5">
                {estimate.timeline_phases.map((phase, i) => (
                  <div key={phase.id}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                        <span className="w-4 h-4 rounded-full bg-orcsys-500 text-white flex items-center justify-center font-bold text-[9px] flex-shrink-0">
                          {i + 1}
                        </span>
                        <p className="text-xs text-slate-700 truncate">{phase.name}</p>
                      </div>
                      <span className="text-[11px] font-semibold text-orcsys-600 ml-1.5 flex-shrink-0">
                        {phase.duration_weeks} sem
                      </span>
                    </div>
                    <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden" style={{ marginLeft: '1.375rem' }}>
                      <motion.div
                        className="h-full bg-gradient-to-r from-orcsys-400 to-orcsys-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{
                          width: `${(phase.duration_weeks / maxPhaseWeeks) * 100}%`,
                        }}
                        transition={{ duration: 0.8, delay: i * 0.1 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2.5 border-t border-slate-200 text-center">
                <p className="text-xs font-semibold text-slate-900">
                  Total :{' '}
                  <span className="text-orcsys-500">
                    ~{estimate.total_duration_weeks} sem
                  </span>
                </p>
              </div>
            </motion.div>
          )}

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            className="space-y-3"
          >
            <Button
              onClick={openDownloadModal}
              className="w-full flex items-center justify-center bg-orcsys-500 hover:bg-orcsys-600 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger l'estimation
            </Button>
            <Button.Link
              href={`tel:${COMPANY_INFO.phone}`}
              variant="outline"
              className="w-full flex items-center justify-center"
            >
              <Phone className="w-4 h-4 mr-2" />
              Nous appeler
            </Button.Link>
            <Button.Link
              href="/booking"
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              className="w-full flex items-center justify-center"
            >
              <CalendarCheck className="w-4 h-4 mr-2" />
              Prendre RDV
            </Button.Link>
            <div className="h-px bg-slate-200" />
            <Button
              onClick={onModify}
              variant="ghost"
              className="w-full flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Modifier la sélection
            </Button>
            <Button onClick={onReset} variant="ghost" className="w-full">
              <RotateCcw className="w-4 h-4 mr-2" />
              Recommencer
            </Button>
          </motion.div>
        </div>
      </div>

      {/* ═══ Email Modal ═══ */}
      <AnimatePresence>
        {emailModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => !downloading && setEmailModalOpen(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              {/* Close button */}
              <button
                type="button"
                onClick={() => !downloading && setEmailModalOpen(false)}
                className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-orcsys-100 flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-orcsys-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Télécharger l'estimation
                    </h3>
                    <p className="text-sm text-slate-500">
                      Entrez votre email pour recevoir le PDF
                    </p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleDownloadWithEmail} className="px-6 pb-6">
                <label
                  htmlFor="download-email"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="download-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    autoFocus
                    disabled={downloading}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orcsys-500 focus:border-transparent disabled:opacity-50 transition-all"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Votre email ne sera utilisé que pour cette estimation.
                </p>

                <button
                  type="submit"
                  disabled={downloading || !email.trim()}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-orcsys-500 hover:bg-orcsys-600 text-white font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Télécharger le PDF
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EstimationResult;
